import {
  AssistantInstallationUsage,
  AssistantChangeMode,
  AssistantSettings,
  AssistantStatus,
  AssistantUsage,
  type UpdateAssistantSettingsRequest,
} from "@cloudserver/contracts";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";
import { api } from "../api/client.js";
import { savePreferences } from "./access.js";

/* The assistant's status, the administrators' settings and usage (docs/assistant.md). */

export const assistantKeys = {
  status: ["assistant", "status"] as const,
  settings: ["assistant", "settings"] as const,
  usage: (month: string) => ["assistant", "usage", month] as const,
};

/** Whether the chat button shows: a model is configured and the organisation switched it on. */
export const useAssistantStatus = (enabled = true) =>
  useQuery({
    queryKey: assistantKeys.status,
    queryFn: () => api("/assistant/status", { schema: AssistantStatus }),
    enabled,
    staleTime: 60_000,
  });

/**
 * Changes unlocked for this browser session after a passed step-up, or back
 * to read only (docs/assistant.md, "Changes"); the status shows the mode.
 */
export function useChangeMode() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (mode: "change" | "read") =>
      api("/assistant/change-mode", {
        method: mode === "change" ? "POST" : "DELETE",
        schema: AssistantChangeMode,
      }),
    onSuccess: () => void client.invalidateQueries({ queryKey: assistantKeys.status }),
  });
}

/**
 * The person's own defaults (the settings view): the style of answers
 * («Кратко» / «Стандартно»; null: the organisation's) and the Autopilot's
 * level in new terminal sessions.
 */
export function useSavePreferences() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: savePreferences,
    onSuccess: () => void client.invalidateQueries({ queryKey: assistantKeys.status }),
  });
}

export const useAssistantSettings = () =>
  useQuery({
    queryKey: assistantKeys.settings,
    queryFn: () => api("/assistant/settings", { schema: AssistantSettings }),
  });

export function useSaveAssistantSettings() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (body: UpdateAssistantSettingsRequest) =>
      api("/assistant/settings", { method: "PUT", body, schema: AssistantSettings }),
    onSuccess: (saved) => {
      client.setQueryData(assistantKeys.settings, saved);
      void client.invalidateQueries({ queryKey: assistantKeys.status });
    },
  });
}

export const useAssistantUsage = (month: string) =>
  useQuery({
    queryKey: assistantKeys.usage(month),
    queryFn: () =>
      api(`/assistant/usage?month=${encodeURIComponent(month)}`, { schema: AssistantUsage }),
  });

/** The installation's usage, for its operators (`ASSISTANT_OPERATOR_EMAILS`). */
export const useInstallationUsage = (month: string) =>
  useQuery({
    queryKey: ["assistant", "installation-usage", month] as const,
    queryFn: () =>
      api(`/assistant/installation-usage?month=${encodeURIComponent(month)}`, {
        schema: AssistantInstallationUsage,
      }),
  });

const Named = z.object({ name: z.string() });

/** The name of the resource a page shows, read with the person's rights (null when it is gone). */
export async function resourceName(path: string): Promise<string | null> {
  try {
    return (await api(path, { schema: Named })).name;
  } catch {
    return null;
  }
}
